export type Environment = "development" | "production" | "staging" | "preview";

function required(envVar: string, defaultValue?: string) {
  let res = typeof process != "undefined" && process.env[envVar];

  // see getEnvVarScriptTag
  if (!res && typeof window != "undefined") {
    res = (window as any).env?.[envVar];
  }

  if (!res) {
    if (
      defaultValue != undefined &&
      (envVar == "NODE_ENV" || getEnvironment() != "production")
    ) {
      res = defaultValue;
    } else {
      throw new Error(`Missing env var ${envVar}`);
    }
  }

  return res;
}

function optional(envVar: string, defaultValue: string): string {
  let res: string | false | undefined =
    typeof process != "undefined" && process.env[envVar];

  if (!res && typeof window != "undefined") {
    res = (window as any).env?.[envVar];
  }

  return (res as string) || defaultValue;
}

const environment = required("NODE_ENV", "development") as Environment;

export function getEnvironment(): Environment {
  return environment;
}

/** This script tag should be included on each page to transfer the environment variables
 * we need on the client side.
 */
export function getEnvVarScriptTag(
  customVars?: string[],
  getValue?: (envVar: string) => string
) {
  if (typeof window != "undefined") {
    return "";
  }

  // this list of variables will probably be expanded later.
  const clientSideEnvVars = [
    "NODE_ENV",
    "IMGIX_BASE_URL",
    "IMGIX_TOKEN",
    "USE_BUNNY_CDN_CENTRA",
    "BUNNY_CDN_CENTRA_BASE_URL",
    "USE_BUNNY_CDN_USERFILES",
    "BUNNY_CDN_USERFILES_BASE_URL",
    "BUNNY_CDN_PRODUCTS_BASE_URL",
    "SENTRY_DSN",
    "SENTRY_RELEASE",
    "GTM_CONTAINER_ID",
  ].concat(customVars || []);

  const env: Record<string, string> = clientSideEnvVars.reduce(
    (env, envVar) => ({
      ...env,
      [envVar]: getValue ? getValue(envVar) : process.env[envVar],
    }),
    {}
  );

  return `<script>window.env = ${JSON.stringify(env)}</script>`;
}

export const getStoryblokToken = () =>
  required("STORYBLOK_TOKEN", "9xu5JN1YLvQ5tgGQSnrR0wtt");

export function getStoryblokVersion(): "draft" | "published" {
  const defaultValue = "draft";

  const res = required("STORYBLOK_VERSION", defaultValue);

  return ["draft", "published"].find((ver) => ver === res)
    ? (res as "draft" | "published")
    : defaultValue;
}

export const getStoryblokEnvId = () =>
  parseInt(required("STORYBLOK_ENV_ID", "158087"));

export const getStoryblokImageUrl = () =>
  `https://a.storyblok.com/f/${getStoryblokEnvId()}`;

/* CommerceHub API Configuration */
export const getCommerceHubApiUrl = () =>
  required("COMMERCEHUB_API_URL", "http://localhost:5030");

export const getCommerceHubApiKey = () =>
  required("COMMERCEHUB_API_KEY", "");

export const getCommerceHubWebhookSecret = () =>
  required("COMMERCEHUB_WEBHOOK_SECRET", "");

export const getRedisUrl = () => required("REDIS_URL", "");

export const getImgIxBaseUrl = () =>
  required("IMGIX_BASE_URL", "https://xw.imgix.net/");

export const getImgIxToken = () => required("IMGIX_TOKEN", "78KKv6xK2KqG7T4u");

export const getImgIxAzureBaseUrl = () =>
  required("IMGIX_AZURE_BASE_URL", "https://xwuf.imgix.net/");

export const getUseBunnyCdnCentra = () =>
  optional("USE_BUNNY_CDN_CENTRA", "false") === "true";

export const getBunnyCdnCentraBaseUrl = () =>
  optional("BUNNY_CDN_CENTRA_BASE_URL", "https://xw-centra.b-cdn.net");

export const getUseBunnyCdnUserFiles = () =>
  optional("USE_BUNNY_CDN_USERFILES", "false") === "true";

export const getBunnyCdnUserFilesBaseUrl = () =>
  optional("BUNNY_CDN_USERFILES_BASE_URL", "https://xw-uf.b-cdn.net");

// Dedicated product-image pull zone (#225 — Centra shutdown migration). Catalog image
// URLs point straight at this zone; the scaler only appends Optimizer transform params.
export const getBunnyCdnProductsBaseUrl = () =>
  optional("BUNNY_CDN_PRODUCTS_BASE_URL", "https://xw-products.b-cdn.net");

export const getAzureBlobConnectionString = () =>
  required("AZURE_BLOB_CONNECTION_STRING");

export const getKlarnaApiHost = () =>
  required("KLARNA_API_HOST", "https://api.playground.klarna.com");
export const getKlarnaPassword = () =>
  required("KLARNA_PASSWORD", "klarna_test_api_byVGYVpyOVVjNSlFWk1TQXZoL0gkYm9OOTVvNVdJancsOTdkYTVmODUtZTU0MC00NDk2LTkzOWItNGFjMTBmMzg1YTE5LDEsOS91eldiMXpDNlk3Yzlxck5JeUkvSDd3VjNRVk84RXkxaE9tUWRzUzAyST0");
export const getKlarnaUsername = () =>
  required("KLARNA_USERNAME", "97da5f85-e540-4496-939b-4ac10f385a19");

export const getLedyerWithAccountStoreId = () =>
  required("LEDYER_STORE_ID", "421423100");
export const getLedyerWithAccountClientId = () => required("LEDYER_CLIENT_ID");
export const getLedyerWithAccountClientSecret = () =>
  required("LEDYER_CLIENT_SECRET");

export const getLedyerWithoutAccountStoreId = () =>
  required("LEDYER_NOT_LOGGED_IN_STORE_ID", "427278874");
export const getLedyerWithoutAccountClientId = () =>
  required("LEDYER_NOT_LOGGED_IN_CLIENT_ID", "cl_2IUC4vBLtzs6fBtPb8TuvRIhrGL");
export const getLedyerWithoutAccountClientSecret = () =>
  required(
    "LEDYER_NOT_LOGGED_IN_CLIENT_SECRET",
    "BamsTAGxDtGpsLRULprTyIvOcjNxw1"
  );

// Ledyer Access credentials can be overridden per store with
// LEDYER_ACCESS_CLIENT_ID_<CC> / LEDYER_ACCESS_CLIENT_SECRET_<CC> (e.g. _FI).
// Stores without an override use the unsuffixed pair.
export const getLedyerAccessCredentials = (countryCode: string) => {
  const suffix = countryCode.toUpperCase();
  const clientId = optional(`LEDYER_ACCESS_CLIENT_ID_${suffix}`, "");
  const clientSecret = optional(`LEDYER_ACCESS_CLIENT_SECRET_${suffix}`, "");

  // A half-set override would silently pair one store's id with another's secret.
  if (!clientId != !clientSecret) {
    throw new Error(
      `LEDYER_ACCESS_CLIENT_ID_${suffix} and LEDYER_ACCESS_CLIENT_SECRET_${suffix} must be set together`
    );
  }

  if (clientId) {
    return { clientId, clientSecret };
  }

  return {
    clientId: required("LEDYER_ACCESS_CLIENT_ID"),
    clientSecret: required("LEDYER_ACCESS_CLIENT_SECRET"),
  };
};
export const getLedyerDomain = () =>
  required("LEDYER_DOMAIN", "sandbox.ledyer.com");
export const getJWTSecret = () =>
  required("JWT_SECRET", "replacemeinproduction");

export const getYotpoAppKey = () => required("YOTPO_APP_KEY");
export const getYotpoSecret = () => required("YOTPO_SECRET");
export const getYotpoCdnEndpoint = () =>
  required("YOTPO_CDN_URL", `https://api-cdn.yotpo.com`);
export const getYotpoCoreEndpoint = () =>
  required("YOTPO_CORE_URL", `https://api.yotpo.com/core/v3/stores`);

export const getKlaviyoBaseUrl = () => required("KLAVIYO_BASE_URL", "https://a.klaviyo.com/api");
export const getKlaviyoApiVersion = () => required("KLAVIYO_API_VERSION", "2024-07-15");
export const getKlaviyoApiKey = () => required("KLAVIYO_API_KEY", "pk_96f92aea60d4c86130bf763f175f07307d");

export const doSendNotifications = () => required("SEND_SMS", "") === "true";

export const getRuleApiKey = () => required("RULE_API_KEY");

/* SMS is sent via 46elks (replaces Rule SMS — see issue #790).
   Docs: https://46elks.com/docs/send-sms */
export const getElksApiUsername = () => required("ELKS_API_USERNAME");
export const getElksApiPassword = () => required("ELKS_API_PASSWORD");
/* Alphanumeric sender ID shown to the recipient (1–11 chars, a letter, no
   spaces). optional() so it defaults everywhere; required() would throw in
   production when unset. */
export const getSmsSender = () => optional("SMS_SENDER", "XWorkwear");

export const getSentryDsn = () => required("SENTRY_DSN", "");

/* Optional release identifier (e.g. git SHA) so Sentry can group errors by deploy
   and resolve source maps. Uses optional() — NOT required() — because required()
   THROWS in production when the var is unset (it only honours the default outside
   production), which would crash Sentry.init(). Empty => Sentry leaves release unset. */
export const getSentryRelease = () => optional("SENTRY_RELEASE", "");

export const getLogtailSourceToken = () => required("LOGTAIL_SOURCE_TOKEN", "");

export const getGtmContainerId = () => required("GTM_CONTAINER_ID", "");

export const getXWEndpointToken = () => required("XW_ENDPOINT_TOKEN", "");

export const getMailgunApiKey = () => required("MAILGUN_API_KEY");

/* Ingrid Delivery Checkout Configuration */
export const getIngridApiHost = () =>
  required("INGRID_API_HOST", "https://api-stage.ingrid.com");

export const getIngridApiToken = () =>
  required("INGRID_API_TOKEN", "MGIyNTY0YzhhYjI1NDE0YThiOGE0NDhkYjkxNmY3OGI=");